import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './EventTemplates.css';

const EventTemplates = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const templates = [
    {
      id: 1,
      name: 'Birthday Party',
      category: 'personal',
      description: 'Celebrate a special day with friends and family',
      icon: '🎂',
      color: '#FF6B9D',
      defaultFields: {
        title: 'Birthday Celebration',
        description: 'Join us for a memorable birthday celebration!',
        category: 'Birthday',
        estimatedBudget: 5000,
      }
    },
    {
      id: 2,
      name: 'Wedding Ceremony',
      category: 'personal',
      description: 'Plan the perfect wedding celebration',
      icon: '💍',
      color: '#FFB6C1',
      defaultFields: {
        title: 'Wedding Celebration',
        description: 'We joyfully invite you to celebrate our wedding day.',
        category: 'Wedding',
        estimatedBudget: 50000,
      }
    },
    {
      id: 3,
      name: 'Corporate Meeting',
      category: 'business',
      description: 'Professional business meetings and conferences',
      icon: '💼',
      color: '#4A90E2',
      defaultFields: {
        title: 'Corporate Meeting',
        description: 'Important business meeting to discuss quarterly goals.',
        category: 'Business',
        estimatedBudget: 10000,
      }
    },
    {
      id: 4,
      name: 'Team Building',
      category: 'business',
      description: 'Strengthen team bonds and collaboration',
      icon: '🤝',
      color: '#50C878',
      defaultFields: {
        title: 'Team Building Event',
        description: 'Join us for an exciting team building activity!',
        category: 'Corporate',
        estimatedBudget: 15000,
      }
    },
    {
      id: 5,
      name: 'Graduation Party',
      category: 'personal',
      description: 'Celebrate academic achievements',
      icon: '🎓',
      color: '#FFD700',
      defaultFields: {
        title: 'Graduation Celebration',
        description: 'Celebrate this important milestone with us!',
        category: 'Graduation',
        estimatedBudget: 8000,
      }
    },
    {
      id: 6,
      name: 'Product Launch',
      category: 'business',
      description: 'Launch your product with a bang',
      icon: '🚀',
      color: '#9B59B6',
      defaultFields: {
        title: 'Product Launch Event',
        description: 'Be the first to experience our latest innovation!',
        category: 'Business',
        estimatedBudget: 25000,
      }
    },
    {
      id: 7,
      name: 'Baby Shower',
      category: 'personal',
      description: 'Welcome the newest family member',
      icon: '👶',
      color: '#87CEEB',
      defaultFields: {
        title: 'Baby Shower',
        description: 'Join us as we celebrate the upcoming arrival!',
        category: 'Baby Shower',
        estimatedBudget: 4000,
      }
    },
    {
      id: 8,
      name: 'Networking Event',
      category: 'business',
      description: 'Connect with professionals in your industry',
      icon: '🌐',
      color: '#FF8C42',
      defaultFields: {
        title: 'Networking Event',
        description: 'Expand your professional network and make valuable connections.',
        category: 'Networking',
        estimatedBudget: 7000,
      }
    },
    {
      id: 9,
      name: 'Anniversary',
      category: 'personal',
      description: 'Commemorate a special milestone',
      icon: '💝',
      color: '#E91E63',
      defaultFields: {
        title: 'Anniversary Celebration',
        description: 'Join us in celebrating years of love and togetherness.',
        category: 'Anniversary',
        estimatedBudget: 12000,
      }
    },
    {
      id: 10,
      name: 'Workshop',
      category: 'business',
      description: 'Educational and skill-building sessions',
      icon: '📚',
      color: '#00BCD4',
      defaultFields: {
        title: 'Workshop Session',
        description: 'Learn new skills and enhance your expertise.',
        category: 'Workshop',
        estimatedBudget: 5000,
      }
    },
    {
      id: 11,
      name: 'Charity Fundraiser',
      category: 'social',
      description: 'Raise funds for a good cause',
      icon: '❤️',
      color: '#FF5722',
      defaultFields: {
        title: 'Charity Fundraiser',
        description: 'Join us in making a difference in our community.',
        category: 'Charity',
        estimatedBudget: 20000,
      }
    },
    {
      id: 12,
      name: 'Webinar',
      category: 'business',
      description: 'Online educational event',
      icon: '💻',
      color: '#3F51B5',
      defaultFields: {
        title: 'Online Webinar',
        description: 'Join our virtual session on important industry topics.',
        category: 'Webinar',
        estimatedBudget: 2000,
      }
    },
  ];

  const categories = [
    { id: 'all', name: 'All Templates', icon: '📋' },
    { id: 'personal', name: 'Personal', icon: '🎉' },
    { id: 'business', name: 'Business', icon: '💼' },
    { id: 'social', name: 'Social', icon: '🌟' },
  ];

  const filteredTemplates = selectedCategory === 'all'
    ? templates
    : templates.filter(t => t.category === selectedCategory);

  const handleUseTemplate = (template) => {
    // Store template data in localStorage to pre-fill CreateEvent form
    localStorage.setItem('selectedTemplate', JSON.stringify(template.defaultFields));
    navigate('/create-event');
  };

  return (
    <div className="event-templates">
      <div className="templates-header">
        <h1 className="templates-title">Event Templates</h1>
        <p className="templates-subtitle">
          Choose a template to quickly create your event with pre-filled details
        </p>
      </div>

      <div className="templates-categories">
        {categories.map(cat => (
          <button
            key={cat.id}
            className={`category-btn ${selectedCategory === cat.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat.id)}
          >
            <span className="category-icon">{cat.icon}</span>
            <span className="category-name">{cat.name}</span>
          </button>
        ))}
      </div>

      <div className="templates-grid">
        {filteredTemplates.map(template => (
          <div
            key={template.id}
            className="template-card"
            style={{ '--template-color': template.color }}
          >
            <div className="template-icon" style={{ backgroundColor: template.color }}>
              {template.icon}
            </div>
            <div className="template-content">
              <h3 className="template-name">{template.name}</h3>
              <p className="template-description">{template.description}</p>
            </div>
            <button
              className="template-use-btn"
              onClick={() => handleUseTemplate(template)}
            >
              Use Template
            </button>
          </div>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="templates-empty">
          <p>No templates found in this category</p>
        </div>
      )}
    </div>
  );
};

export default EventTemplates;
